/**
 * @fileoverview Module for `src/AdminRoute.js`.
 */

import axios from 'axios';
import React from 'react';
import propTypes from 'prop-types';
import logger from './lib/logger';
import { Route } from 'react-router-dom';
import Loading from './components/Loading';


// Ensures the logged in user is an admin.  If not, redirects to /
/**
 * Guard component that verifies admin access before rendering a route.
 */
class AdminChecker extends React.Component {
  /**
   * Initializes component state with loading flag and non-admin default.
   */
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      isAdmin: false,
    };
  }


  /**
   * Fetches the current user's profile to determine admin status, updating state accordingly.
   */
  async componentDidMount() {
    const user_id = localStorage.getItem('user_id');
    if (user_id) {
      try {
        const accessToken = await this.props.auth.getAccessToken();
        const res = await axios({
          headers: {
            Authorization: 'Bearer '.concat(accessToken),
          },
          method: 'GET',
          url: `${ process.env.REACT_APP_API_URL }/v1/users/self`,
        });
        logger.error(`Got this response from self: ${ JSON.stringify(res) }`);
        this.setState({
          loading: false,
          isAdmin: res.data.isAdmin,
        });
      } catch (err) {
        this.setState({ loading: false, isAdmin: false });
        logger.error(err);
      }
    } else {
      this.setState({ loading: false, isAdmin: false });
    }
  }

  /**
   * Shows a loading indicator while checking admin status, forces login for non-admins, or renders the wrapped component.
   */
  render() {
    if (this.state.loading) {
      return <Loading { ...this.props } />;
    } else if (this.state.isAdmin === false) {
      this.props.auth.forceLogin();
    }
    const Component = this.props.component;
    return <Component { ...this.props } />;
  }
}

AdminChecker.propTypes = {
  auth: propTypes.object,
  component: propTypes.oneOfType([propTypes.element, propTypes.func]),
};

// This higher-order component pattern taken from
// https://tylermcginnis.com/react-router-protected-routes-authentication/
/**
 * Wraps a React Router Route to render the given component only after
 * verifying admin authorization via AdminChecker.
 *
 * @param {Object} params
 * @returns {JSX.Element} Protected route.
 */
// eslint-disable-next-line react/prop-types
const AdminRoute = ({ component: Component, auth, ...rest }) => (
  <Route
    { ...rest }
    render={ (props) => (
      <AdminChecker auth={ auth } component={ Component } { ...props } />
    ) }
  />
);

export default AdminRoute;
